import React from 'react'
import ReactPlayer from 'react-player'
import ReactResizeDetector from 'react-resize-detector'

import { Modal, ScreenReaderOnly } from '@acorns/web-components'
import { compose, withHandlers, withState } from 'recompose'
import styled from 'styled-components'

import platform from 'components/Trackers/platform'

import { TestIDs } from '../../test/utils/testIds'

const reactPlayerConfig = {
  file: {
    attributes: { controlsList: 'nodownload' },
  },
}

type Props = {
  isOpen: boolean
  onEnded?: () => void
  onRequestClose: () => void
  videoSrc: string
}

type State = {
  width: number
  height: number
  setWidth: (width: number) => void
  setHeight: (height: number) => void
}

type HandlerProps = {
  handleResize: (viewportWidth: number) => void
}

type MergedProps = Props & State & HandlerProps

const enhance = compose<MergedProps, Props>(
  withState('height', 'setHeight', 450),
  withState('width', 'setWidth', 800),
  withHandlers({
    handleResize: ({ setHeight, setWidth }) => contentWidth => {
      let width
      let height

      if (platform === 'desktop') {
        // Desktop should always render a landscape-optimized video with a width:height
        // ratio of 1.78.  Max width is 800px.
        width = 800
        height = 450

        if (contentWidth < 800) {
          width = Math.floor(contentWidth * 0.8)
          height = Math.floor(width / 1.78)
        }
      } else {
        // Mobile should always render a portrait-optimized video with a width:height
        // ratio of .56.
        const { innerHeight, innerWidth } = window
        const isPortrait = innerHeight >= innerWidth

        if (isPortrait) {
          width = Math.floor(innerWidth * 0.6)
          height = Math.floor(width / 0.56)
        } else {
          height = Math.floor(innerHeight * 0.9)
          width = Math.floor(height * 0.56)
        }
      }

      setWidth(width)
      setHeight(height)
    },
  }),
)

const StyledModal = styled(Modal)`
  width: auto;
  max-width: 800px;
  background: transparent;

  max-height: none;
  padding: 0;
`

const VideoModal = enhance(
  ({ handleResize, height, isOpen, onEnded, onRequestClose, videoSrc, width }) => (
    <>
      <ScreenReaderOnly>Video modal</ScreenReaderOnly>
      <StyledModal
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        accessibilityProperties={{ labelledBy: 'videoModal' }}
      >
        <ReactPlayer
          data-testid={TestIDs.REACT_PLAYER_VIDEO}
          config={reactPlayerConfig}
          controls
          height={height}
          playing={isOpen}
          url={videoSrc}
          width={width}
          onEnded={onEnded}
        />
      </StyledModal>

      <ReactResizeDetector
        handleWidth
        handleHeight
        onResize={handleResize}
        resizableElementId="app"
      />
    </>
  ),
)

export default VideoModal
